import { apiCall } from './api';
import { CustomError, logError } from './sentry';
import { getCountry } from './country';
import { convertValueToCurrency } from './currency';
import { currencyForCountry } from './currency-region';
import { updateObj, getObj } from './localStorage';

// Product SKU constants
export const DEFAULT_MEMBERSHIP_SKU = '2'; // Basic membership SKU
export const STARTER_SET_SKU = '201'; // Starter set product SKU
export const MEMORY_BOX_SKU = '222'; // Memory box product SKU

/**
 * Get currency code for a country. The map lives in currency-region.js
 * alongside the trust rules that decide which country gets used at all —
 * two copies could disagree about what counts as a non-euro market.
 */
export const getCurrencyForCountry = currencyForCountry;

/**
 * KL-14636: whether a product is actually sellable in a given country,
 * per the catalog's `activeCountry` map — the same field selectGift.js/
 * createGift.js already gate on for gift-flow country filtering. The
 * membership/subscription checkout funnel never checked it, so a SKU
 * marked unavailable (or never configured) for a country could still
 * reach checkout with broken pricing.
 *
 * Mirrors klikkie-orders' resolvePerCountryValue fallback ladder
 * (country-specific -> EU bucket) rather than selectGift.js's plain
 * direct lookup, so this can't regress smaller EU markets (e.g. AT, IT,
 * PL) that have no country-specific entry and rely on the EU bucket to
 * resolve pricing correctly today. A product with no `activeCountry`
 * field at all fails open — it never opted into country gating, so
 * there's no signal to block on.
 */
export const isProductActiveInCountry = (product, countryCode) => {
  const activeCountry = product?.activeCountry;
  if (!activeCountry) return true;
  if (countryCode in activeCountry) return Boolean(activeCountry[countryCode]);
  if ('EU' in activeCountry) return Boolean(activeCountry.EU);
  return true;
};

export const getEnvironment = () => {
  /* eslint-disable no-undef */
  if (typeof __KLIKKIE_ENV__ !== 'undefined') {
    return __KLIKKIE_ENV__ === 'prod' ? 'production' : 'dev';
  }
  /* eslint-enable no-undef */
  const env = getObj('env').env || 'production';
  return env === 'localProd' ? 'production' : env;
};

export const getProductPackages = async (start = 0, end = 2000) => {
  const [{ getDatabase, ref: dbRef, orderByKey, query: dbQuery, startAt, endAt, get: dbGet }, { initFirebase }] =
    await Promise.all([
      import(/* webpackChunkName: "firebase-db" */ 'firebase/database'),
      import('./firebaseApp'),
    ]);

  const app = initFirebase();
  const database = getDatabase(app);
  const productPackagesRef = dbRef(database, 'productPackages_I18n');
  const s = start.toString();
  const e = end.toString();
  const q = dbQuery(productPackagesRef, orderByKey(), startAt(s), endAt(e));

  return dbGet(q)
    .then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        updateObj('productPackages', data);
        return data;
      }
      return null;
    })
    .catch((error) => {
      console.error('Error getting data:', error);
    });
};

export const getGifts = () => getProductPackages(100, 199);
export const getSubscriptions = () => getProductPackages(0, 99);

export const getProductPackage = async (sku, environment) => {
  try {
    const devPrefix = environment === 'production' ? '' : 'dev.';
    const url = `https://${devPrefix}api.klikkie.com/products/${sku}`;
    const { data } = await apiCall(url, 'GET');
    return data;
  } catch (error) {
    logError(error, { sku, environment });
    throw new CustomError('Failed to get product type', 'warning');
  }
};

/**
 * Try to get product price from pre-loaded data before hitting the API.
 *
 * Resolution order:
 *  1. window.__KLIKKIE_PRICES (injected at build time via inline script)
 *  2. /funnel/prices.json   (static file on same-origin CDN)
 *  3. Live API call          (original behaviour, kept as fallback)
 *
 * KL-15111: step 1 is the normal path in production. build.js inlines the
 * SKUs each page renders (site/scripts/inline-prices.js) from the same
 * prices.json blobs step 2 would fetch, so the two can't disagree — step 2
 * now only serves SKUs a page resolves at runtime (funnel plan pickers).
 */

// Module-level cache so /funnel/prices.json is fetched at most once.
let _staticPricesPromise = null;

const fetchStaticPrices = () => {
  if (!_staticPricesPromise) {
    _staticPricesPromise = (
      typeof window !== 'undefined' && window.__KLIKKIE_PREFETCH?.prices
        ? window.__KLIKKIE_PREFETCH.prices
        : fetch('/funnel/prices.json').then((r) => {
            if (!r.ok) throw new Error(`prices.json ${r.status}`);
            return r.json();
          })
    ).catch((err) => {
      console.warn('Static prices unavailable, will use API:', err.message || err);
      _staticPricesPromise = null; // allow retry next time
      return null;
    });
  }
  return _staticPricesPromise;
};

// KL-15620: in-flight API lookups, keyed by sku+environment, so one render pass
// asks for a SKU once. bindSmartPrices maps every `.smartprice` element through
// its own getSmartPrice in a single Promise.all, and /products/photobooks has
// 331 of them for SKU 604 — without this that is 331 simultaneous identical
// requests whenever the API is the resolver (any dev build, and on prod the
// SKUs deliberately kept out of the snapshot). Entries are dropped as soon as
// they settle: this deduplicates a burst, it does not cache. A promotion that
// ends between renders must still disappear on the next pass (KL-15093).
const _inFlightPrices = new Map();

const getProductPrice = async (sku, environment) => {
  // Both build-time caches are snapshots of the *production* product DB, so a
  // dev bundle has to skip them and talk to dev.api.klikkie.com.
  if (environment === 'production') {
    // 1. window.__KLIKKIE_PRICES — inlined per page at build time, synchronous.
    if (typeof window !== 'undefined' && window.__KLIKKIE_PRICES?.[sku]) {
      return window.__KLIKKIE_PRICES[sku];
    }

    // 2. Static prices.json (same-origin CDN hit), for SKUs not on the page.
    try {
      const staticPrices = await fetchStaticPrices();
      if (staticPrices?.[sku]) {
        return staticPrices[sku];
      }
    } catch {
      // fall through to API
    }
  }

  // 3. Fallback: live API call
  const key = `${environment}:${sku}`;
  if (!_inFlightPrices.has(key)) {
    const devPrefix = environment === 'production' ? '' : 'dev.';
    const url = `https://${devPrefix}api.klikkie.com/products/${sku}`;
    const request = apiCall(url, 'GET')
      .then(({ data: priceInfo }) => priceInfo)
      .finally(() => _inFlightPrices.delete(key));
    _inFlightPrices.set(key, request);
  }

  try {
    return await _inFlightPrices.get(key);
  } catch (error) {
    logError(error, { sku, environment });
    throw new CustomError('Failed to get product price', 'warning');
  }
};

/**
 * @param {string|number} sku
 * @param {boolean|string} showCurrency
 * @param {string|null} forCountry - resolve against this country instead of
 *   asking getCountry(). Callers re-rendering in response to `klikkie:country`
 *   MUST pass it: getCountry() can itself write storage and re-dispatch that
 *   event, so a listener that called it would feed its own trigger.
 */
export const getSmartPrice = async (sku, showCurrency = false, forCountry = null) => {
  try {
    const countryCode = forCountry || (await getCountry());
    const currency = getCurrencyForCountry(countryCode);
    const nodeEnv = getEnvironment();
    const priceInfo = await getProductPrice(sku, nodeEnv);
    const price = priceInfo.price[countryCode] || priceInfo.price.EU;
    // KL-15093: when a promotion is active the products API injects
    // `discountedPrice` (same country-key shape as `price`, cents) and an
    // informational `discountCode`. Checkout auto-applies the promotion
    // server-side, so the discounted amount is what the customer pays —
    // the website only displays it, never computes it.
    const discountedPrice = priceInfo.discountedPrice
      ? priceInfo.discountedPrice[countryCode] || priceInfo.discountedPrice.EU
      : undefined;
    const hasDiscount = discountedPrice != null && discountedPrice < price;
    const effectivePrice = hasDiscount ? discountedPrice : price;
    let priceMonthly = 0;
    if (priceInfo.priceMonthly) {
      priceMonthly = priceInfo.priceMonthly[countryCode] || priceInfo.priceMonthly.EU;
    }
    const priceMonthlyDiscounted = priceInfo.prepaidMonths
      ? price / priceInfo.prepaidMonths
      : price;
    const discountAbsolute = priceInfo.prepaidMonths ? priceMonthly - priceMonthlyDiscounted : 0;
    // KL-15115: subscription promotions. When a promotion is active the
    // products API also injects `discountedPriceMonthly` (same country-key
    // shape as `priceMonthly`, cents) on products that have `priceMonthly`.
    // Like `discountedPrice`, it is display-only: checkout applies the
    // promotion server-side, the website never computes a discount itself.
    // Only treated as a sale when it is actually lower than the monthly
    // amount shown today (prepaid-effective monthly), so a stale or equal
    // value can never invent a fake discount.
    const discountedPriceMonthly = priceInfo.discountedPriceMonthly
      ? priceInfo.discountedPriceMonthly[countryCode] || priceInfo.discountedPriceMonthly.EU
      : undefined;
    const hasMonthlyDiscount =
      discountedPriceMonthly != null && discountedPriceMonthly < priceMonthlyDiscounted;
    const effectiveMonthly = hasMonthlyDiscount ? discountedPriceMonthly : priceMonthlyDiscounted;
    // Calculate discount percentage: promotion discount wins, otherwise the
    // prepaid-months monthly comparison (existing subscription behaviour).
    let discountPercentage = 0;
    if (hasDiscount) {
      discountPercentage = Math.round(((price - discountedPrice) / price) * 100);
    } else if (discountAbsolute > 0 && priceMonthly > 0) {
      discountPercentage = Math.round((discountAbsolute / priceMonthly) * 100);
    }

    // Original price before discount: full (undiscounted) price for API
    // promotions, prepaid total for subscription plans, else current price.
    let priceOriginal = price;
    if (!hasDiscount && priceInfo.prepaidMonths && priceMonthly > 0) {
      priceOriginal = priceMonthly * priceInfo.prepaidMonths;
    }

    // Helper to convert with the correct currency
    const convert = (value) => convertValueToCurrency(value, showCurrency, currency);

    return {
      price: convert(effectivePrice),
      priceRaw: effectivePrice, // Raw price in cents for calculations
      priceRegularRaw: price, // KL-15107: without promotion, what renewals are charged
      priceOriginal: convert(priceOriginal),
      hasDiscount,
      discountCode: priceInfo.discountCode,
      priceMonthly: convert(effectiveMonthly),
      priceMonthlyOriginal: convert(priceMonthly),
      // KL-15115: the monthly amount as displayed without the promotion —
      // what sale presentation strikes through next to `priceMonthly`.
      priceMonthlyBeforeDiscount: convert(priceMonthlyDiscounted),
      hasMonthlyDiscount,
      discountAbsolute: convert(discountAbsolute),
      discountPercentage,
      priceMonthlyRaw: effectiveMonthly, // Raw value for comparisons
      currency, // Include currency code
    };
  } catch (error) {
    logError(error, { sku, showCurrency });
    throw new CustomError('Failed to get smart price', 'warning');
  }
};

/**
 * KL-15115: markup for a generic `.smartprice` element given a getSmartPrice
 * result. When the element shows a monthly price and the API flagged a
 * subscription promotion, render the pre-promotion monthly struck through
 * next to the discounted monthly (same pattern as the KL-15093 photobook
 * PDP sale pills). Every other case returns exactly what the binder set
 * before this change, so pages without a promotion stay byte-identical.
 * Styling is inline because these elements appear on arbitrary pages that
 * share no promo stylesheet; the class is a styling hook only.
 */
export const renderSmartPriceHtml = (type, prices, currentHtml) => {
  if (type === 'priceMonthly' && prices.hasMonthlyDiscount) {
    const strike =
      'text-decoration:line-through;opacity:.6;font-size:.85em;font-weight:400;margin-right:.25em';
    return (
      `<span class="smartprice-original" style="${strike}">${prices.priceMonthlyBeforeDiscount}</span>` +
      `${prices.priceMonthly}`
    );
  }
  return prices[type] || currentHtml;
};

/**
 * Compute base price (excluding shipping) and total shipping for a gift order.
 *
 * @param {Object} params
 * @param {number} params.units - Number of main gift units.
 * @param {string|number} params.mainSku - SKU of the main gift product.
 * @param {Array<string|number>} params.accessorySkus - SKUs of selected accessories.
 * @param {Object} params.productPriceData - Map of SKU -> product pricing data.
 * @param {string} params.countryCode - Country code for pricing.
 * @returns {{ unitPrice: number, totalProductAmount: number, totalShipping: number }}
 */
export const computeGiftTotals = ({
  units,
  mainSku,
  accessorySkus,
  productPriceData,
  countryCode,
}) => {
  const safeUnits = Number(units) > 0 ? Number(units) : 1;

  const mainProduct = productPriceData[mainSku];
  if (!mainProduct) {
    return {
      unitPrice: 0,
      totalProductAmount: 0,
      totalShipping: 0,
    };
  }

  const mainPrice = mainProduct.price[countryCode] || 0;
  const mainShippingPerOrder = mainProduct.shippingCost?.[countryCode] || 0;

  // price is the base product price (shipping is NOT included).
  // Shipping is charged once per order, not per unit.
  let totalProductAmount = safeUnits * mainPrice;
  let totalShipping = mainShippingPerOrder;

  (accessorySkus || []).forEach((sku) => {
    const product = productPriceData[sku];
    if (!product) return;

    const accessoryPrice = product.price[countryCode] || 0;
    const accessoryShipping = product.shippingCost?.[countryCode] || 0;

    // Accessories scale with the number of units, but their shipping
    // is only charged once per order.
    totalProductAmount += safeUnits * accessoryPrice;
    totalShipping += accessoryShipping;
  });

  return {
    unitPrice: mainPrice,
    totalProductAmount,
    totalShipping,
  };
};
